import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { ApiFehler } from "../../api/client";
import { usePortalAnlage, usePortalEinreichen } from "../../api/hooks";
import type { PortalAnlageDetail } from "../../api/typen";
import { Hinweis, Karte, Knopf, Spinner } from "../../komponenten/Basis";
import { KundenLinkKarte } from "../../komponenten/portal/KundenLinkKarte";
import { StatusLinkKarte } from "../../komponenten/portal/StatusLinkKarte";
import { KARTEN, UnterlagenKarten } from "../../komponenten/portal/Unterlagen";
import { zeitpunkt } from "../../lib/format";

function Kopf({ anlage, schritt }: { anlage: PortalAnlageDetail; schritt?: string }) {
  return (
    <div>
      <Link to="/portal" className="text-sm font-semibold text-neutral-600 hover:underline">← Alle Anmeldungen</Link>
      {schritt && <p className="mt-3 text-sm font-semibold text-neutral-500">{schritt}</p>}
      <h1 className={`${schritt ? "" : "mt-3 "}text-2xl font-semibold tracking-tight`}>{anlage.kunde_name}</h1>
      {anlage.ort && <p className="text-sm text-neutral-500">{anlage.ort}</p>}
    </div>
  );
}

/** Schritt 2 und 3: Unterlagen sammeln, dann absenden. */
function Entwurf({ anlage }: { anlage: PortalAnlageDetail }) {
  const einreichen = usePortalEinreichen(anlage.id);
  const [zusammenfassung, setZusammenfassung] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const vorhanden = (typ: string) => anlage.dokumente.filter((d) => d.typ === typ).length;
  const fehlend = KARTEN.filter((k) => k.pflicht && vorhanden(k.typ) === 0);

  function absenden() {
    setFehler(null);
    einreichen.mutate(undefined, {
      onError: (e) => setFehler(e instanceof ApiFehler ? e.message : "Das hat nicht geklappt. Bitte erneut versuchen."),
    });
  }

  if (!zusammenfassung) {
    return (
      <div className="space-y-5 pb-28">
        <Kopf anlage={anlage} schritt="Schritt 2 von 3" />
        <KundenLinkKarte anlageId={anlage.id} />
        <p className="text-lg font-semibold">Laden Sie die Unterlagen hoch</p>
        <UnterlagenKarten anlageId={anlage.id} dokumente={anlage.dokumente} />
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-neutral-200 bg-white/95 px-4 py-3 backdrop-blur">
          <div className="mx-auto max-w-3xl">
            <Knopf variante="primaer" className="w-full !py-4 !text-base" disabled={anlage.dokumente.length === 0} onClick={() => setZusammenfassung(true)}>
              Weiter
            </Knopf>
            {anlage.dokumente.length === 0 && <p className="mt-1 text-center text-xs text-neutral-500">Bitte laden Sie mindestens ein Dokument hoch.</p>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Kopf anlage={anlage} schritt="Schritt 3 von 3" />
      <p className="text-lg font-semibold">Alles bereit zum Absenden?</p>
      <Karte className="divide-y divide-neutral-100">
        {KARTEN.map((k) => {
          const n = vorhanden(k.typ);
          return (
            <div key={k.typ} className="flex items-center gap-3 px-4 py-3">
              <span className={n > 0 ? "font-bold text-ampel-gruen" : k.pflicht ? "font-bold text-ampel-gelb" : "text-neutral-400"} aria-hidden>
                {n > 0 ? "✓" : k.pflicht ? "!" : "–"}
              </span>
              <span className="flex-1">{k.titel}</span>
              <span className="text-sm text-neutral-500">{n > 0 ? `${n} ${n === 1 ? "Datei" : "Dateien"}` : k.pflicht ? "fehlt" : "keine"}</span>
            </div>
          );
        })}
      </Karte>
      {fehlend.length > 0 && (
        <Hinweis art="info">
          Es fehlt noch etwas ({fehlend.map((k) => k.titel).join(", ")}). Sie können trotzdem absenden und die Unterlagen jederzeit nachreichen.
        </Hinweis>
      )}
      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      <div className="grid gap-2">
        <Knopf variante="primaer" className="w-full !py-4 !text-base" laedt={einreichen.isPending} onClick={absenden}>
          Anmeldung einreichen
        </Knopf>
        <Knopf variante="geist" className="w-full" onClick={() => setZusammenfassung(false)}>Zurück zu den Unterlagen</Knopf>
      </div>
    </div>
  );
}

/** Eingereichte Anmeldung: Stand ansehen und jederzeit weitere Unterlagen nachreichen. */
function Eingereicht({ anlage }: { anlage: PortalAnlageDetail }) {
  return (
    <div className="space-y-5">
      <Kopf anlage={anlage} />
      <Karte className="p-5">
        <p className="text-sm text-neutral-500">Stand</p>
        <p className="mt-1 text-xl font-semibold">{anlage.status_text}</p>
        {anlage.eingereicht_am && <p className="mt-1 text-sm text-neutral-500">Eingereicht am {zeitpunkt(anlage.eingereicht_am)}</p>}
        {anlage.status === "eingegangen" && <p className="mt-3 text-sm text-neutral-700">Wir kümmern uns darum. Sie müssen nichts weiter tun.</p>}
      </Karte>
      <StatusLinkKarte anlageId={anlage.id} vorhanden={anlage.status_link_vorhanden} />
      {anlage.status !== "fertig" && <KundenLinkKarte anlageId={anlage.id} />}
      <div>
        <h2 className="text-lg font-semibold">Unterlagen ergänzen</h2>
        <p className="mb-3 text-sm text-neutral-600">Sie können jederzeit weitere Fotos und Dokumente hochladen.</p>
        <UnterlagenKarten anlageId={anlage.id} dokumente={anlage.dokumente} />
      </div>
    </div>
  );
}

export function AnmeldungSeite() {
  const id = Number(useParams().id);
  const abfrage = usePortalAnlage(id);

  if (abfrage.isLoading) return <div className="flex justify-center py-24"><Spinner label="Wird geladen…" /></div>;
  if (abfrage.error || !abfrage.data) {
    const nichtGefunden = abfrage.error instanceof ApiFehler && abfrage.error.status === 404;
    return (
      <div className="space-y-4">
        <Hinweis art="fehler">{nichtGefunden ? "Diese Anmeldung gibt es nicht." : (abfrage.error?.message ?? "Die Anmeldung konnte nicht geladen werden.")}</Hinweis>
        <Link to="/portal" className="inline-block text-sm font-semibold underline">Zur Übersicht</Link>
      </div>
    );
  }
  return abfrage.data.status === "entwurf" ? <Entwurf anlage={abfrage.data} /> : <Eingereicht anlage={abfrage.data} />;
}
